import axios from "axios";
import utils from "../../utils";
import Vue from "vue";
import { DateTime } from "luxon";

const defs = {
  altitude: {
    name: "altitude",
    color: "steelblue",
    measure: false,
    margin: 38,
    fillOpacity: 0.2,
    getter: (l) => l.altitude,
    format: (v) => utils.formatNumber(v, 0)
  },
  speed: {
    name: "speed",
    color: "salmon",
    measure: false,
    margin: 28,
    fillOpacity: 0.2,
    getter: (l) => l.speed,
    format: (v) => utils.formatNumber(v, 0)
  },
  km: {
    name: "km",
    color: "sandybrown",
    measure: true,
    margin: 50,
    fillOpacity: 0.2,
    getter: (l) => l.measure.km / 1000,
    format: (v) => utils.formatNumber(v, 0)
  },
  tank: {
    name: "tank",
    color: "mediumseagreen",
    measure: true,
    margin: 30,
    fillOpacity: 0.2,
    getter: (l) => l.measure.tank,
    format: (v) => utils.formatNumber(v, 0)
  },
  weight: {
    name: "weight",
    color: "brown",
    measure: true,
    margin: 38,
    fillOpacity: 0.2,
    getter: (l) => l.measure.weight / 1000,
    format: (v) => utils.formatNumber(v, 1)
  },
  rpm: {
    name: "rpm",
    color: "darkviolet",
    measure: true,
    margin: 35,
    fillOpacity: 0.2,
    getter: (l) => l.measure.rpm,
    format: (v) => utils.formatNumber(v, 0)
  },
  temp1: {
    name: "temp1",
    color: "teal",
    measure: true,
    margin: 30,
    fillOpacity: 0.2,
    getter: (l) => l.measure.temp1,
    format: (v) => utils.formatNumber(v, 1)
  },
  temp2: {
    name: "temp2",
    color: "turquoise",
    measure: true,
    margin: 30,
    fillOpacity: 0.2,
    getter: (l) => l.measure.temp2,
    format: (v) => utils.formatNumber(v, 1)
  }
};

const state = {
  /**
   * Definice zobrazených parametrů
   */
  defs: defs,
  /**
   * Vybrané parametry pro zobrazení (max. 4)
   */
  selected: null,
  /**
   *  Poslední parametry pro získání historie
   */
  query: null,
  /**
   * Vybrané vozidlo
   */
  vehicleId: null,
  /**
   * Vybrané vozidlo
   */
  vehicle: null,
  /**
   * Zobrazit všechy jízdy.
   * Implicitně se zonbrazují pouze nenulové.
   */
  all: false,
  /**
   * Období počátek.
   * Období je většinou jeden den.
   */
  periodFrom: null,
  /**
   * Období konec.
   * Období je většinou jeden den.
   */
  periodTo: null,
  /**
   * Jízdy pro vybarné obodbí
   */
  rides: [],
  /**
   * SUmarní infromace za vybrané období
   */
  summary: null,
  /**
   * Pozice pro vybarné obodbí
   */
  locations: [],
  /**
   * Typy parametrů, které byli již vyčteny v měřeních
   */
  parametersFetched: [],
  /**
   * Aktuálně vybraná jízda
   */
  ride: null,
  fetching: false,
  fetched: null,
  running: null
};

const getters = {
  canPrevRide: state => {
    if (state.rides === null || state.rides.length === 0)
      return false;

    if (state.ride === null)
      return true;
    else {
      let index = state.rides.findIndex(x => x.fromTimestamp === state.ride.fromTimestamp);
      return index > 0;
    }
  },
  canNextRide: state => {
    if (state.rides === null || state.rides.length === 0)
      return false;

    if (state.ride === null)
      return true;
    else {
      let index = state.rides.findIndex(x => x.fromTimestamp === state.ride.fromTimestamp);
      return index < state.rides.length - 1;
    }
  }
};

const actions = {
  clear({ commit, state }) {
    if (state.fetching)
      return Promise.resolve();

    if (state.vehicel !== null || state.periodFrom !== null || state.periodTo !== null || state.ride !== null || state.locations !== null) {
      commit("historyFetched", {
        query: null,
        vehicle: null,
        periodFrom: null,
        periodTo: null,
        rides: null,
        ride: null,
        locations: null
      });
    }
  },

  clearParameters({ commit }) {
    commit("setParameters", []);
  },

  selectParameter({ commit, state }, code) {
    if (state.selected.length >= 4 || state.selected.includes(code))
      return;

    const selected = [...state.selected];
    selected.push(code);
    commit("setParameters", selected);
  },

  clearParameter({ commit, state }, code) {
    if (state.selected.length === 0 || !state.selected.includes(code))
      return;

    commit("setParameters", state.selected.filter(x => x !== code));
  },

  toggleParameter({ commit, state, dispatch }, code) {
    if (state.selected.includes(code)) {
      commit("setParameters", state.selected.filter(x => x !== code));
    } else if (state.selected.length < 4) {
      const selected = [...state.selected];
      selected.push(code);
      commit("setParameters", selected);

      dispatch("fetch", state.query);
    }
  },

  prevRide({ dispatch, state }) {
    if (state.rides === null || state.rides.length === 0)
      return Promise.resolve();

    if (state.ride === null) {
      dispatch("fetch", {
        vehicle: state.vehicle,
        periodFrom: state.periodFrom,
        periodTo: state.periodTo,
        rideFrom: state.rides[state.rides.length - 1].fromTimestamp,
        last: false
      });
    } else {
      let index = state.rides.findIndex(x => x.fromTimestamp === state.ride.fromTimestamp);
      if (index > 0)
        dispatch("fetch", {
          vehicle: state.vehicle,
          periodFrom: state.periodFrom,
          periodTo: state.periodTo,
          rideFrom: state.rides[index - 1].fromTimestamp,
          last: false
        });
    }
  },

  nextRide({ dispatch, state }) {
    if (state.rides === null || state.rides.length === 0)
      return Promise.resolve();

    if (state.ride === null) {
      dispatch("fetch", {
        vehicle: state.vehicle,
        periodFrom: state.periodFrom,
        periodTo: state.periodTo,
        rideFrom: state.rides[0].fromTimestamp,
        last: false
      });
    } else {
      let index = state.rides.findIndex(x => x.fromTimestamp === state.ride.fromTimestamp);
      if (index < state.rides.length - 1)
        dispatch("fetch", {
          vehicle: state.vehicle,
          periodFrom: state.periodFrom,
          periodTo: state.periodTo,
          rideFrom: state.rides[index + 1].fromTimestamp,
          last: false
        });
    }
  },

  refresh({ state, dispatch }) {
    if (state.query !== null) {
      state.query["refresh"] = true;
      dispatch("fetch", state.query);
    }
  },

  /**
   * Stáhní historii vodila podle zadaných parametrů.
   * Parametry: {
   *   vehicle: string,     - objekt vozidla
   *   periodFrom: string,  - datum a čas od
   *   periodTo: string,    - datum a čas do
   *   rideFrom: string     - vybrat jízdu (datum a čas)
   *   last: boolean,       - pokud jizda neexistuje, vyber posledni jízdu ze seznamu
   *  }
   *
   * @param params Parametry
   */
  fetch({ commit, state, dispatch }, params) {
    // pokud se vozidla právě vyčítaji, nic nedělěj
    if (state.fetching)
      return Promise.resolve();

    if (state.selected === null) {
      state.selected = Vue.$cookies.get("history-graphs");

      if (state.selected === null || state.selected === undefined)
        state.selected = ["altitude"];
    }

    try {
      if (params.vehicleId === null || params.vehicleId === undefined) {
        if (state.vehicleId !== null) {
          // pokud není vozidlo zadáno použij stávající (ale pouze pokud je zadáno)
          params.vehicleId = state.vehicleId;
          params.vehicle = state.vehicle;
        } else {
          commit("historyFetched", {});
          return Promise.resolve();
        }
      }

      const all = (params.all !== undefined && params.all !== null) ? params.all : state.all;
      var change = (state.vehicleId !== params.vehicleId) || ((params.refresh !== undefined) ? params.refresh : false);
      var rides = null;
      var locations = null;
      var ride_from = null;
      var ride = null;
      var parametersFetched = [];
      let tid;

      if (params.periodFrom === null || params.periodTo === null) {
        commit("historyFetched", {
          query: params,
          vehicleId: params.vehicleId,
          vehicle: params.vehicle,
          periodFrom: null,
          periodTo: null,
          rides: [],
          ride: null,
          locations: [],
          parametersFetched: []
        });

        return Promise.resolve();
      }

      // nastavime, ze se objekt s touto URL uz stahuje, aby se to neptalo nekolikrat
      commit("beginFetching");

      // zavolame stazeni objektu, ktere az se provede, tak se objekt ulozi
      return dispatch("progress/add", "Fetch record", { root: true })
        .then(id => {
          tid = id;
          const promises = [];

          // načti jízdy pouze pokud se změnilo období
          if (change || state.periodFrom == null || state.periodFrom !== params.periodFrom
            || state.periodTo == null || state.periodTo !== params.periodTo
            || state.all !== all) {
            change = true;
            const filter = (all) ? "all" : "common";

            if (params.vehicle == null)
              promises.push(axios.get(`/v1/{#company#}/vehicle/${params.vehicleId}`)
                .then((response) => {
                  params.vehicle = response.data;
                  return Promise.resolve();
                }).catch((error) => {
                  commit("historyFetched", {});
                  console.log("Vehicle fetch error", error);
                  return undefined;
                }));

            // načti jízdy
            promises.push(axios.get(`/v1/{#company#}/vehicle/${params.vehicleId}/ride?from=${encodeURIComponent(DateTime.fromISO(params.periodFrom).toFormat(utils.API_DTFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.periodTo).toFormat(utils.API_DTFORMAT))}&filter=${filter}&size=500`)
              .then((response) => {
                rides = response.data.content;
                return Promise.resolve();
              }).catch((error) => {
                commit("historyFetched", {});
                console.log("Vehicle rides fetch error", error);
                return undefined;
              }));

            parametersFetched = state.selected.filter(x => x in defs && defs[x].measure);

            promises.push(axios.get(`/v1/{#company#}/vehicle/${params.vehicleId}/locations?from=${encodeURIComponent(DateTime.fromISO(params.periodFrom).toFormat(utils.API_DTFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.periodTo).toFormat(utils.API_DTFORMAT))}&fromRides=true&params=${parametersFetched.join(",")}&size=5000`)
              .then((response) => {
                locations = response.data.content;
                return Promise.resolve();
              }).catch((error) => {
                commit("historyFetched", {});
                console.log("Vehicle locations fetch error", error);
                return undefined;
              }));
          } else {
            const paramsDiff = state.selected.filter(x => x in defs && defs[x].measure && !state.parametersFetched.includes(x));

            if (paramsDiff.length > 0) {
              parametersFetched = state.parametersFetched.concat(paramsDiff);
              rides = state.rides;
              change = true;

              promises.push(axios.get(`/v1/{#company#}/vehicle/${params.vehicleId}/locations?from=${encodeURIComponent(DateTime.fromISO(params.periodFrom).toFormat(utils.API_DTFORMAT))}&to=${encodeURIComponent(DateTime.fromISO(params.periodTo).toFormat(utils.API_DTFORMAT))}&fromRides=true&params=${parametersFetched.join(",")}&size=5000`)
                .then((response) => {
                  locations = response.data.content;
                  return Promise.resolve();
                }).catch((error) => {
                  commit("historyFetched", {});
                  console.log("Vehicle locations fetch error", error);
                  return undefined;
                }));
            }
          }

          if (promises.length > 0)
            return Promise.all(promises);
          else {
            rides = state.rides;
            return Promise.resolve();
          }
        })
        .then(() => {
          if (rides.length === 0) {
            // pokud neexistují žádné jízdy, nepokračuj a ulož stav
            commit("historyFetched", {
              query: params,
              vehicleId: params.vehicleId,
              vehicle: params.vehicle,
              all: all,
              periodFrom: params.periodFrom,
              periodTo: params.periodTo,
              rides: [],
              locations: locations,
              ride: null,
              parametersFetched: parametersFetched
            });
          } else if (change || state.ride === null || state.ride.fromTimestamp !== params.rideFrom) {
            // načti pozice jízdy pokud:
            // 1. změna období
            // 2. není vyplněná jízda
            // 3. je vybrana jina jízda

            // najdi vybranou jízdu v načtených jídách, jinak vyber první
            var fride = rides.filter(x => x.fromTimestamp === params.rideFrom);
            var ride_change = false;

            if (fride.length > 0) {
              ride = fride[0];
              ride_from = ride.fromTimestamp;
              if (state.ride === null || state.ride.fromTimestamp !== ride_from)
                ride_change = true;
            } else if (state.ride !== null)
              ride_change = true;

            if (change) {
              commit("historyFetched", {
                query: params,
                vehicleId: params.vehicleId,
                vehicle: params.vehicle,
                all: all,
                periodFrom: params.periodFrom,
                periodTo: params.periodTo,
                rides: rides,
                ride: ride,
                locations: locations,
                parametersFetched: parametersFetched
              });
            } else if (ride_change) {
              commit("rideChanged", {
                query: params,
                ride: ride
              });
            }

            // zruš předchozí mód
            this.dispatch("online/mapMode", "history");

            return Promise.resolve();
          }
        })
        .catch((e) => {
          console.log("Fetch error: ", e);
        })
        .finally(() => {
          commit("fetched");
          dispatch("progress/remove", tid, { root: true });
        });
    } catch {
      commit("fetched");
      return Promise.resolve();
    }
  }
};

const mutations = {
  setParameters(state, params) {
    Vue.$cookies.set("history-graphs", params, "14d");
    Vue.set(state, "selected", params);
  },

  historyFetched(state, params) {
    Vue.set(state, "query", params.query || null);
    Vue.set(state, "vehicleId", params.vehicleId || null);
    Vue.set(state, "vehicle", params.vehicle || null);
    Vue.set(state, "all", (params.all !== undefined || params.all !== null) ? params.all : state.all);
    Vue.set(state, "periodFrom", params.periodFrom || null);
    Vue.set(state, "periodTo", params.periodTo || null);
    Vue.set(state, "rides", params.rides || null);
    Vue.set(state, "locations", params.locations || null);
    Vue.set(state, "ride", params.ride || null);
    Vue.set(state, "parametersFetched", params.parametersFetched || []);

    // spočítej sumární inforamce
    if (params.rides !== null && params.rides.length > 0) {
      const summary = {};

      summary.from = params.rides[0].locationFrom;
      summary.to = params.rides[params.rides.length - 1].locationTo;
      summary.kmStop = params.rides[params.rides.length - 1].kmStop;
      summary.distance = 0;
      summary.engineHours = 0;
      summary.consumptionDiff = 0;
      summary.climb = 0;
      summary.descent = 0;
      summary.maxSpeed = 0;
      summary.driverQuality = 0;
      var drvq_count = 0;

      for (var i = 0; i < params.rides.length; i++) {
        const ride = params.rides[i];

        ride.order = i + 1;
        summary.distance += ride.distance;
        summary.engineHours += ride.engineHours;
        if (ride.consumptionDiff !== null)
          summary.consumptionDiff += ride.consumptionDiff;
        summary.climb += ride.climb;
        summary.descent += ride.descent;

        if (ride.maxSpeed > summary.maxSpeed)
          summary.maxSpeed = ride.maxSpeed;

        if (ride.driverQuality !== null && ride.driverQuality !== undefined) {
          summary.driverQuality += ride.driverQuality;
          drvq_count += 1;
        }
      }

      if (drvq_count > 0)
        summary.driverQuality = summary.driverQuality / drvq_count;

      Vue.set(state, "summary", summary);
    } else
      Vue.set(state, "summary", null);
  },

  rideChanged(state, params) {
    Vue.set(state, "query", params.query || null);
    Vue.set(state, "ride", params.ride);
  },

  /**
   * Nastaví začátek stahování
   */
  beginFetching(state) {
    Vue.set(state, "fetching", true);
  },

  fetched(state) {
    Vue.set(state, "fetching", false);
    Vue.set(state, "fetched", DateTime.now());
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};